// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { z } from "zod";
import type { components } from "@/api/schema";
import { type FieldSource, normalizeHeader } from "@/lib/spreadsheet";

type UserFlag = components["schemas"]["UserFlagEnum"];

export const LOCAL_REALM = "local";

/** User-group flag string that flips on the Field Tool's FUE (first-user-experience) training flow. */
export const FT_FUE_TRAINING_FLAG: UserFlag = "ft_fue_training_required";

/** User-group flag that forces the user through a password change on next login. */
export const REQUIRE_PASSWORD_RESET_FLAG: UserFlag = "require_password_reset";

/** Minimum password length the API enforces on non-admin callers creating local-realm users. */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * The FT FUE training flag is only relevant for ops users. A group qualifies when "ops" is one of
 * its underscore-delimited segments (e.g. `focal_ops`, `ops_field`) — a plain substring match would
 * wrongly catch names like `dev_opsy`.
 */
export function groupsAllowFueTraining(groups: string[]): boolean {
  return groups.some((name) => name.split("_").includes("ops"));
}

const PASSWORD_CHARS = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*";

/** A random 16-character password (the create-user dialog's dice button, and bulk "Computed"). */
export function generatePassword(): string {
  const array = new Uint32Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (v) => PASSWORD_CHARS[v % PASSWORD_CHARS.length]).join("");
}

export type UserImportFieldKey = "email" | "fullName" | "userName" | "realm" | "password" | "groups" | "defaultStore";

export type FieldSources = Record<UserImportFieldKey, FieldSource>;

/** Constant-mode editor style — mirrors the single create-user dialog's controls. */
export type UserConstantInput = "text" | "password" | "realm" | "groups" | "store";

export interface UserImportField {
  key: UserImportFieldKey;
  required: boolean;
  /** Normalised header tokens that auto-map to this field (besides the key itself). */
  aliases: string[];
  constantInput: UserConstantInput;
  /** Offers `computed` mode (password → a random one generated per user). */
  computable?: boolean;
}

/** Target create-user fields the sheet columns map onto, in display order. */
export const USER_IMPORT_FIELDS: UserImportField[] = [
  { key: "email", required: true, aliases: ["emailaddress", "mail"], constantInput: "text" },
  { key: "fullName", required: true, aliases: ["name", "displayname"], constantInput: "text" },
  { key: "userName", required: false, aliases: ["username", "login", "user"], constantInput: "text" },
  { key: "realm", required: true, aliases: ["sso", "ssorealm"], constantInput: "realm" },
  // Required only for local-realm rows — enforced per row, not at config time.
  { key: "password", required: false, aliases: ["pass", "pwd"], constantInput: "password", computable: true },
  { key: "groups", required: true, aliases: ["group", "usergroups", "roles"], constantInput: "groups" },
  {
    key: "defaultStore",
    required: true,
    aliases: ["store", "storename", "defaultstoreid", "defaultstorename"],
    constantInput: "store",
  },
];

/** Field → i18n label key (reuse the single create-user form labels). */
export const USER_FIELD_LABEL_KEY: Record<UserImportFieldKey, string> = {
  email: "users.form.email",
  fullName: "users.form.fullName",
  userName: "users.form.username",
  realm: "users.form.realm",
  password: "users.form.password",
  groups: "users.form.groups",
  defaultStore: "users.form.defaultStore",
};

/**
 * Initial per-field sources for a set of headers: auto-map columns by normalised name/alias (first
 * match wins, never reusing a column). Unmatched fields fall back to sensible defaults — realm to the
 * `local` constant, password to computed (generated), groups to `defaultGroups` when given — and
 * everything else to an empty column source for the user to fill in.
 */
export function initialFieldSources(headers: string[], defaultGroups: string[] = []): FieldSources {
  const normalizedHeaders = headers.map(normalizeHeader);
  const used = new Set<number>();
  const sources = {} as FieldSources;
  for (const field of USER_IMPORT_FIELDS) {
    const candidates = new Set([normalizeHeader(field.key), ...field.aliases.map(normalizeHeader)]);
    const index = normalizedHeaders.findIndex((header, i) => !used.has(i) && header !== "" && candidates.has(header));
    if (index !== -1) {
      used.add(index);
      sources[field.key] = { mode: "column", column: index, value: "" };
    } else if (field.key === "realm") {
      sources[field.key] = { mode: "constant", column: null, value: LOCAL_REALM };
    } else if (field.key === "password") {
      sources[field.key] = { mode: "computed", column: null, value: "" };
    } else if (field.key === "groups" && defaultGroups.length > 0) {
      sources[field.key] = { mode: "constant", column: null, value: joinList(defaultGroups) };
    } else {
      sources[field.key] = { mode: "column", column: null, value: "" };
    }
  }
  return sources;
}

/**
 * Resolve a field's raw value for a row. Computed password resolves to "" — the generated value is
 * per-run state owned by the caller (see `buildUserCreateBody`), never derived from the row.
 */
export function resolveField(row: string[], sources: FieldSources, key: UserImportFieldKey): string {
  const source = sources[key];
  if (source.mode === "constant") return source.value.trim();
  if (source.mode === "column") return source.column != null ? (row[source.column] ?? "").trim() : "";
  return "";
}

/** Split a multi-value cell (`ops, field;admin|x` or one per line) into trimmed, de-duplicated items. */
export function splitList(value: string): string[] {
  return [
    ...new Set(
      value
        .split(/[,;|\n]/)
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  ];
}

/** Inverse of `splitList` — how the constant groups multi-select is stored in a `FieldSource`. */
export function joinList(values: string[]): string {
  return values.join(", ");
}

/** The slice of a store the default-store lookup needs. */
export interface StoreRef {
  storeName: string;
  legibleName?: string;
}

/**
 * Build a lookup that resolves a sheet's default-store cell to a store name. Sheets carry whatever
 * people recognise, so the exact store name wins, then a case-insensitive store name or display name.
 */
export function buildStoreResolver(stores: StoreRef[]): (value: string) => string | null {
  const exact = new Set<string>();
  const loose = new Map<string, string>();
  for (const store of stores) {
    exact.add(store.storeName);
    loose.set(store.storeName.toLowerCase(), store.storeName);
  }
  // Display names only fill gaps, so they never shadow another store's actual name.
  for (const store of stores) {
    const key = store.legibleName?.trim().toLowerCase();
    if (key && !loose.has(key)) loose.set(key, store.storeName);
  }
  return (value) => {
    const raw = value.trim();
    if (!raw) return null;
    if (exact.has(raw)) return raw;
    return loose.get(raw.toLowerCase()) ?? null;
  };
}

const emailSchema = z.email();

export interface ValidationContext {
  /** Known realms / groups — an empty list skips that check (context not loaded). */
  realms: string[];
  groups: string[];
  resolveStore: (value: string) => string | null;
  /** Admins may set short passwords; the API enforces the minimum for everyone else. */
  isAdmin: boolean;
  /** Translate an error key with interpolation values. */
  t: (key: string, values?: Record<string, string | number>) => string;
}

/**
 * Per-row validation, so rows the API would reject are flagged in the preview rather than failing
 * mid-import: required values, email shape, known realm / groups / store, a password for local-realm
 * rows, and no duplicate email or username within the file (the API checks against existing users).
 */
export function validateRows(rows: string[][], sources: FieldSources, ctx: ValidationContext): string[][] {
  const { t } = ctx;
  const label = (key: UserImportFieldKey) => t(USER_FIELD_LABEL_KEY[key]);
  const knownRealms = new Set(ctx.realms);
  const knownGroups = new Set(ctx.groups);
  const seenEmails = new Set<string>();
  const seenUserNames = new Set<string>();
  return rows.map((row) => {
    const errors: string[] = [];
    for (const field of USER_IMPORT_FIELDS) {
      if (field.required && !resolveField(row, sources, field.key)) {
        errors.push(t("bulkImport.errors.missing", { field: label(field.key) }));
      }
    }

    const email = resolveField(row, sources, "email");
    if (email) {
      if (!emailSchema.safeParse(email).success) errors.push(t("users.bulkCreate.errors.badEmail", { email }));
      const key = email.toLowerCase();
      if (seenEmails.has(key)) errors.push(t("users.bulkCreate.errors.dupEmail", { email }));
      else seenEmails.add(key);
    }

    const userName = resolveField(row, sources, "userName");
    if (userName) {
      const key = userName.toLowerCase();
      if (seenUserNames.has(key)) errors.push(t("users.bulkCreate.errors.dupUserName", { userName }));
      else seenUserNames.add(key);
    }

    const realm = resolveField(row, sources, "realm");
    if (realm && knownRealms.size > 0 && !knownRealms.has(realm)) {
      errors.push(t("users.bulkCreate.errors.unknownRealm", { realm }));
    }

    if (realm === LOCAL_REALM && sources.password.mode !== "computed") {
      const password = resolveField(row, sources, "password");
      if (!password) errors.push(t("bulkImport.errors.missing", { field: label("password") }));
      else if (!ctx.isAdmin && password.length < MIN_PASSWORD_LENGTH) {
        errors.push(t("users.form.validation.passwordMinLength"));
      }
    }

    if (knownGroups.size > 0) {
      for (const group of splitList(resolveField(row, sources, "groups"))) {
        if (!knownGroups.has(group)) errors.push(t("users.bulkCreate.errors.unknownGroup", { group }));
      }
    }

    const store = resolveField(row, sources, "defaultStore");
    if (store && !ctx.resolveStore(store)) errors.push(t("users.bulkCreate.errors.unknownStore", { store }));

    return errors;
  });
}

export interface CreateOptions {
  /** Resolved store name for the row's default store. */
  storeName: string;
  /** Generated password, used when the password source is computed. */
  generatedPassword?: string;
  requirePasswordReset: boolean;
  ftFueTraining: boolean;
}

/**
 * Build a UsersCreateRequest body from a row. The password (and the reset flag it implies) is only
 * sent for local-realm users; the FUE flag only when the row's groups include an ops group, matching
 * what the single create-user dialog would let you submit.
 */
export function buildUserCreateBody(
  row: string[],
  sources: FieldSources,
  options: CreateOptions,
): components["schemas"]["UsersCreateRequest"] {
  const realm = resolveField(row, sources, "realm");
  const groups = splitList(resolveField(row, sources, "groups"));
  const isLocal = realm === LOCAL_REALM;
  const password =
    sources.password.mode === "computed" ? options.generatedPassword : resolveField(row, sources, "password");
  const flags: UserFlag[] = [];
  if (isLocal && options.requirePasswordReset) flags.push(REQUIRE_PASSWORD_RESET_FLAG);
  if (options.ftFueTraining && groupsAllowFueTraining(groups)) flags.push(FT_FUE_TRAINING_FLAG);
  return {
    email: resolveField(row, sources, "email"),
    userName: resolveField(row, sources, "userName") || undefined,
    fullName: resolveField(row, sources, "fullName"),
    realm,
    password: isLocal && password ? password : undefined,
    groups,
    defaultStoreId: options.storeName,
    flags: flags.length ? flags : undefined,
  };
}
